package com.alpha.showcase.common.ui.ai

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.alpha.ai.imagegeneration.AiCapability
import com.alpha.showcase.common.ai.*
import com.alpha.showcase.common.components.BackHandler
import com.alpha.showcase.common.theme.Dimen
import com.alpha.showcase.common.ui.TvIconButton
import com.alpha.showcase.common.ui.TvIconToggleButton
import com.alpha.showcase.common.ui.tvScreenFocus
import io.ktor.http.Url
import getPlatform
import isWeb
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import showcaseapp.composeapp.generated.resources.*

@Composable
internal fun AiProviderDialog(engineOverride: AiEngine? = null, onDismiss: () -> Unit) {
    AiProviderPage(engineOverride, inDialog = true, onDismiss = onDismiss)
}

/** Shared page content; generation flows can also open it in a modal container. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun AiProviderPage(
    engineOverride: AiEngine? = null,
    inDialog: Boolean = false,
    initialCapability: AiCapability = AiCapability.IMAGE_TO_IMAGE,
    onDismiss: () -> Unit,
) {
    if (!aiFeaturesAvailable(isWeb())) return
    val engine = remember(engineOverride) { engineOverride ?: AiServices.engine }
    val library by engine.library.collectAsState()
    val scope = rememberCoroutineScope()
    val generationVisible = LocalAiGenerationVisible.current
    val capabilities = if (generationVisible) listOf(AiCapability.IMAGE_TO_IMAGE, AiCapability.IMAGE_UNDERSTANDING)
        else listOf(AiCapability.IMAGE_UNDERSTANDING)
    var capability by remember(initialCapability, generationVisible) {
        mutableStateOf(initialCapability.takeIf { it in capabilities } ?: AiCapability.IMAGE_UNDERSTANDING)
    }
    var editing by remember { mutableStateOf(false) }
    var existing by remember { mutableStateOf<AiProfile?>(null) }
    var deleting by remember { mutableStateOf<AiProfile?>(null) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<StringResource?>(null) }
    var ready by remember { mutableStateOf(false) }
    var showServiceInfo by rememberSaveable { mutableStateOf(false) }
    val profiles = library.activeProfiles.filter { aiProviderCapability(it.providerId) == capability }
    val selected = if (capability == AiCapability.IMAGE_TO_IMAGE) library.generationProfileId else library.understandingProfileId
    val dismiss = { if (!busy) onDismiss() }
    LaunchedEffect(engine) {
        try { engine.initialize(); ready = true }
        catch (e: CancellationException) { throw e }
        catch (_: Exception) { message = Res.string.ai_profile_error_load_failed }
    }
    fun updateProfile(failure: StringResource, action: suspend () -> Unit) {
        busy = true
        message = null
        scope.launch {
            try { action() }
            catch (e: CancellationException) { throw e }
            catch (_: Exception) { message = failure }
            finally { busy = false }
        }
    }
    AiProviderContainer(inDialog, busy, dismiss) {
        Scaffold(Modifier.fillMaxSize().tvScreenFocus(), topBar = {
            AiPageTopBar(
                title = stringResource(Res.string.ai_provider_settings_title),
                onBack = dismiss,
                inDialog = inDialog,
                backEnabled = !busy,
                actions = {
                    TvIconButton(onClick = { showServiceInfo = true }) {
                        Icon(Icons.Outlined.Info, stringResource(Res.string.ai_service_info_title))
                    }
                },
            )
        }) { padding ->
            Box(Modifier.fillMaxSize().padding(padding).consumeWindowInsets(padding).imePadding(), contentAlignment = Alignment.TopCenter) {
                Column(Modifier.widthIn(max = AiPageContentMaxWidth).fillMaxSize()) {

                    if (capabilities.size == 1) {
                        Row(
                            modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp).semantics { heading() },
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Icon(
                                if (capability == AiCapability.IMAGE_TO_IMAGE) Icons.Outlined.AutoFixHigh else Icons.Outlined.AutoAwesome,
                                contentDescription = null,
                                modifier = Modifier.size(20.dp),
                                tint = MaterialTheme.colorScheme.primary,
                            )
                            Text(stringResource(capability.label()), style = MaterialTheme.typography.titleMedium)
                        }
                    } else {
                        Text(stringResource(Res.string.ai_configuration_description), Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
                            style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        PrimaryTabRow(selectedTabIndex = capabilities.indexOf(capability),
                            modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp).clip(RoundedCornerShape(16.dp)),
                            containerColor = MaterialTheme.colorScheme.surfaceContainer, divider = {}) {
                            capabilities.forEach { value ->
                                Tab(selected = capability == value, enabled = !busy && !editing, onClick = { capability = value; message = null },
                                    text = { Text(stringResource(value.label()), maxLines = 1, overflow = TextOverflow.Ellipsis) },
                                    icon = { Icon(if (value == AiCapability.IMAGE_TO_IMAGE) Icons.Outlined.AutoFixHigh else Icons.Outlined.AutoAwesome, null) })
                            }
                        }
                    }
                    Column(Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState()).padding(20.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        if (capability == AiCapability.IMAGE_UNDERSTANDING) AiSummaryPreview()
                        AiMessage(message)
                        if (!ready && message == null) {
                            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
                        }
                        if (ready) SavedAiProfiles(profiles, selected, enabled = !busy && !editing,
                            profileName = { it.displayName(engine.client) },
                            showFacePrivacy = capability == AiCapability.IMAGE_UNDERSTANDING,
                            facePrivacyEnabled = library.facePrivacyEnabled,
                            onFacePrivacyChanged = { enabled ->
                                updateProfile(Res.string.ai_profile_error_save_failed) { engine.setFacePrivacyEnabled(enabled) }
                            },
                            onNew = { existing = null; editing = true },
                            onSelect = { id -> updateProfile(Res.string.ai_profile_error_save_failed) { engine.selectProfile(id) } },
                            onEdit = { existing = it; editing = true }, onDelete = { deleting = it })
                        if (ready && capability == AiCapability.IMAGE_UNDERSTANDING) {
                            AiSummaryArchiveActions(engine, enabled = !busy && !editing, onBusyChanged = { busy = it })
                        }
                        if (ready && !getPlatform().persistsLocalData) {
                            Text(stringResource(Res.string.ai_local_storage_reclaimable),
                                style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                }
            }
        }
        if (showServiceInfo) AiServiceInfoDialog(onDismiss = { showServiceInfo = false })
        if (editing) AiProfileEditorDialog(engine, capability, existing, onDismiss = { editing = false })
        deleting?.let { profile ->
            AlertDialog(onDismissRequest = { deleting = null },
                icon = { Icon(Icons.Outlined.DeleteOutline, null) },
                title = { Text(stringResource(Res.string.ai_profile_delete_confirm)) },
                text = { Text(stringResource(Res.string.ai_profile_delete_pending_tasks, profile.displayName(engine.client))) },
                confirmButton = { Button(onClick = {
                    deleting = null
                    updateProfile(Res.string.ai_profile_error_archive_failed) { engine.archiveProfile(profile.id) }
                }) { Text(stringResource(Res.string.delete)) } },
                dismissButton = { TextButton(onClick = { deleting = null }) { Text(stringResource(Res.string.cancel)) } })
        }
    }
}

@Composable
private fun AiServiceInfoDialog(onDismiss: () -> Unit) {
    AlertDialog(
        onDismissRequest = onDismiss,
        icon = { Icon(Icons.Outlined.Info, contentDescription = null) },
        title = { Text(stringResource(Res.string.ai_service_info_title)) },
        text = {
            Column(Modifier.verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                listOf(
                    Res.string.ai_service_info_photos_title to Res.string.ai_service_info_photos_body,
                    Res.string.ai_service_info_security_title to Res.string.ai_service_info_security_body,
                    Res.string.ai_service_info_disclaimer_title to Res.string.ai_service_info_disclaimer_body,
                ).forEach { (title, body) ->
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(stringResource(title), style = MaterialTheme.typography.titleSmall)
                        Text(stringResource(body), style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
        },
        confirmButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.close)) } },
    )
}

@Composable
private fun AiProviderContainer(inDialog: Boolean, busy: Boolean, onDismiss: () -> Unit, content: @Composable () -> Unit) {
    if (inDialog) {
        Dialog(onDismissRequest = onDismiss, properties = DialogProperties(
            usePlatformDefaultWidth = false, dismissOnBackPress = !busy, dismissOnClickOutside = false,
        ), content = content)
    } else {
        BackHandler(enabled = busy) { true }
        content()
    }
}

@Composable
private fun SavedAiProfiles(
    profiles: List<AiProfile>, selected: String?, enabled: Boolean,
    profileName: (AiProfile) -> String,
    showFacePrivacy: Boolean, facePrivacyEnabled: Boolean, onFacePrivacyChanged: (Boolean) -> Unit,
    onNew: () -> Unit, onSelect: (String) -> Unit, onEdit: (AiProfile) -> Unit, onDelete: (AiProfile) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.ai_saved_profile), Modifier.weight(1f), style = MaterialTheme.typography.titleSmall)
            if (showFacePrivacy) {
                TvIconToggleButton(checked = facePrivacyEnabled, onCheckedChange = onFacePrivacyChanged,
                    enabled = enabled) {
                    Icon(painterResource(if (facePrivacyEnabled) Res.drawable.ic_face_privacy_checked else Res.drawable.ic_face_privacy),
                        contentDescription = stringResource(Res.string.ai_image_summary_face_privacy))
                }
            }
            TextButton(onClick = onNew, enabled = enabled) {
                Icon(Icons.Outlined.Add, null)
                Text(stringResource(Res.string.ai_new_configuration), Modifier.padding(start = 6.dp))
            }
        }
        if (showFacePrivacy && facePrivacyEnabled) {
            Text(stringResource(Res.string.ai_image_summary_face_privacy_description),
                modifier = Modifier.padding(horizontal = 4.dp),
                style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        }
        Surface(Modifier.fillMaxWidth().selectableGroup(), shape = RoundedCornerShape(Dimen.textFiledCorners),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)) {
            if (profiles.isEmpty()) Box(
                Modifier.fillMaxWidth().heightIn(min = AiProfileItemMinHeight).padding(horizontal = 16.dp, vertical = 8.dp),
                contentAlignment = Alignment.CenterStart,
            ) {
                Text(stringResource(Res.string.ai_profile_empty_compact),
                    style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            else Column {
                profiles.forEachIndexed { index, profile ->
                    val active = profile.id == selected
                    val name = profileName(profile)
                    ListItem(modifier = Modifier.heightIn(min = AiProfileItemMinHeight)
                        .selectable(active, enabled = enabled, role = Role.RadioButton, onClick = { onSelect(profile.id) }),
                        colors = ListItemDefaults.colors(containerColor = if (active) MaterialTheme.colorScheme.secondaryContainer else Color.Transparent),
                        headlineContent = { Text(name, maxLines = 1, overflow = TextOverflow.Ellipsis) },
                        supportingContent = {
                            Text(if (active) stringResource(Res.string.ai_profile_in_use) else "${profile.host()} · ${profile.model}",
                                maxLines = 1, overflow = TextOverflow.Ellipsis)
                        },
                        leadingContent = { RadioButton(active, onClick = null, enabled = enabled) },
                        trailingContent = {
                            Row {
                                TvIconButton(onClick = { onEdit(profile) }, enabled = enabled) {
                                    Icon(Icons.Outlined.Edit, stringResource(Res.string.ai_profile_edit_named, name))
                                }
                                TvIconButton(onClick = { onDelete(profile) }, enabled = enabled) {
                                    Icon(Icons.Outlined.DeleteOutline, stringResource(Res.string.ai_profile_delete_named, name))
                                }
                            }
                        })
                    if (index < profiles.lastIndex) HorizontalDivider()
                }
            }
        }
    }
}

internal fun AiCapability.label() = if (this == AiCapability.IMAGE_TO_IMAGE) Res.string.ai_capability_image_to_image else Res.string.ai_capability_image_understanding
internal fun AiProfile.host(): String = runCatching { Url(baseUrl).host }.getOrDefault("")

private val AiProfileItemMinHeight = 72.dp
